// src/utils/secureStorage.js
const secureStorage = {
    // Generate encryption key
    generateKey: async () => {
        return window.crypto.subtle.generateKey(
            {
                name: "AES-GCM",
                length: 256
            },
            true,
            ["encrypt", "decrypt"]
        );
    },

    // Get or create encryption key
    getEncryptionKey: async () => {
        // In a real app, you would store this key more securely
        // This is just for demonstration
        let keyString = sessionStorage.getItem('encryptionKey');

        if (!keyString) {
            const key = await secureStorage.generateKey();
            const exportedKey = await window.crypto.subtle.exportKey("raw", key);
            keyString = Array.from(new Uint8Array(exportedKey))
                .map(b => b.toString(16).padStart(2, '0'))
                .join('');
            sessionStorage.setItem('encryptionKey', keyString);
            return key;
        }

        // Convert stored key back to CryptoKey
        const keyBytes = new Uint8Array(keyString.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
        return window.crypto.subtle.importKey(
            "raw",
            keyBytes,
            {name: "AES-GCM", length: 256},
            false,
            ["encrypt", "decrypt"]
        );
    },

    // Encrypt and store data
    setItem: async (key, value) => {
        try {
            const encKey = await secureStorage.getEncryptionKey();
            const iv = window.crypto.getRandomValues(new Uint8Array(12));
            const encodedValue = new TextEncoder().encode(JSON.stringify(value));

            const encryptedData = await window.crypto.subtle.encrypt(
                {
                    name: "AES-GCM",
                    iv: iv
                },
                encKey,
                encodedValue
            );

            // Store IV + encrypted data
            const encryptedArray = new Uint8Array(encryptedData);
            const combinedArray = new Uint8Array(iv.length + encryptedArray.length);
            combinedArray.set(iv);
            combinedArray.set(encryptedArray, iv.length);

            // Convert to base64 without causing stack overflow for large arrays
            let binaryString = '';
            const chunkSize = 8192; // Process in chunks to avoid stack overflow
            for (let i = 0; i < combinedArray.length; i += chunkSize) {
                const chunk = combinedArray.slice(i, i + chunkSize);
                binaryString += String.fromCharCode.apply(null, chunk);
            }
            const base64Data = btoa(binaryString);

            localStorage.setItem(key, base64Data);
            return true;
        } catch (error) {
            console.error('Error encrypting data:', error);
            return false;
        }
    },

    // Get and decrypt data
    // Improved getItem function with better error handling
    getItem: async (key) => {
        try {
            const storedData = localStorage.getItem(key);

            if (!storedData) {
                console.log(`No data found for key: ${key}`);
                return null;
            }

            const encKey = await secureStorage.getEncryptionKey();

            // Convert from base64 to array
            const binaryString = atob(storedData);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            // Extract IV and data
            const iv = bytes.slice(0, 12);
            const encryptedData = bytes.slice(12);

            try {
                const decryptedData = await window.crypto.subtle.decrypt(
                    {
                        name: "AES-GCM",
                        iv: iv
                    },
                    encKey,
                    encryptedData
                );

                const decoder = new TextDecoder();
                const result = JSON.parse(decoder.decode(decryptedData));

                return result;
            } catch (decryptError) {
                console.error(`Decryption error for key ${key}:`, decryptError);

                // If decryption fails, the key might have changed or been invalidated
                // Try to return the raw data as fallback
                try {
                    // For token specifically, try reading it directly if encryption fails
                    if (key === 'token') {
                        const directToken = sessionStorage.getItem('token');
                        if (directToken) {
                            return directToken;
                        }
                    }

                    return JSON.parse(storedData);
                } catch (parseError) {
                    return storedData; // Return as string if parsing fails
                }
            }
        } catch (error) {
            console.error(`Error retrieving data for key ${key}:`, error);
            return null;
        }
    },

    // Synchronous version to check if item exists
    hasItem: (key) => {
        return localStorage.getItem(key) !== null;
    },

    // Remove item
    removeItem: (key) => {
        localStorage.removeItem(key);
    }
};

export default secureStorage;
